import cn from 'classnames'
import { IProgramType } from 'legacy/lut/features/partner360/configuration'
import { useCallback, useState } from 'react'

import { FormCheckBox, FormInput, FormTextarea, useFormField } from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { TFormValidationResult } from 'cng-common/features/form/models'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { Translation } from 'cng-common/features/translations/components/translation'
import { LoaderOverlay } from 'core-components/library/loader'
import { Actions } from 'core-components/library/modalDialog'
import { GlobalPartnerTypes } from 'partner360/widgets/gp360Administration'

import { getJbpGlobalPartnerAdministrationCrud } from '../../../../../../helpers/getJbpGlobalPartnerAdministrationCrud'
import { TRANSLATION_KEYS } from '../../../../../../translations'
import { IGlobalPartnerForm } from '../../../../models/globalPartnerForm'
import { GLOBAL_PARTNER_FORM_KEY } from '../../configs'
import { GlobalPartnerContentTypes } from '../contentTypeButtons'

import { applyGPBasicInfoChanges } from './api/applyGPBasicInfoChanges'
import { GlobalFormManagerSelect } from './components/globalFormManagerSelect/GlobalFormManagerSelect'
import { GlobalFormPbmSelect } from './components/globalFormPbmSelect/GlobalFormPbmSelect'
import { GlobalFormRtmSelect } from './components/globalFormRtmSelect/GlobalFormRtmSelect'
import { OfferingsList } from './components/offerings/OfferingsList'

import styles from './BasicInfo.module.scss'

interface IBasicInfo {
  initialData: IGlobalPartnerForm
  formData: IGlobalPartnerForm
  isValid: boolean
  offerings: IProgramType[]
  onClose: () => void
  currentFYear: number
  onContentTypeChange: (newValue: GlobalPartnerContentTypes) => void
  isNewGlobalPartner: boolean
  globalPartnerType: GlobalPartnerTypes
}

export const BasicInfo = ({
  initialData,
  formData,
  offerings,
  onClose,
  currentFYear,
  isValid,
  onContentTypeChange,
  isNewGlobalPartner,
  globalPartnerType,
}: IBasicInfo) => {
  const [isLoading, setIsLoading] = useState(false)

  const tableCrud = getJbpGlobalPartnerAdministrationCrud()
  const { handleChange: handleGlobalPartnerIdChange } = useFormField({
    defaultValue: formData.globalPartnerId,
    formKey: GLOBAL_PARTNER_FORM_KEY,
    field: 'globalPartnerId',
  })

  const handleValidate = useCallback(async () => {
    const formValidation = FormValidationBuilder.build(GLOBAL_PARTNER_FORM_KEY)
    const errors = await Promise.all(
      [
        formValidation.call('globalPartnerName', formData.globalPartnerName),
        formValidation.call('rtm', formData.rtm),
        formValidation.call('pbm', formData.pbm),
        formValidation.call('managers', formData.managers),
        formValidation.call('offeringsState', formData.offeringsState),
      ].filter((it) => it)
    )
    return errors.filter((it): it is TFormValidationResult => it !== undefined)
  }, [formData])

  const onApply = useCallback(async () => {
    const errors = await handleValidate()
    if (errors.length > 0) {
      return
    }
    setIsLoading(true)
    const newGlobalPartnerId = await applyGPBasicInfoChanges(formData, currentFYear)
    if (newGlobalPartnerId) {
      handleGlobalPartnerIdChange(newGlobalPartnerId)
    }
    tableCrud.cleanCache()
    tableCrud.triggerOnChange()
    onContentTypeChange(GlobalPartnerContentTypes.List)
    setIsLoading(false)
  }, [
    formData,
    currentFYear,
    handleGlobalPartnerIdChange,
    tableCrud,
    handleValidate,
    onContentTypeChange,
  ])

  return (
    <div
      className={cn(styles.root, {
        [styles.isNewGlobalPartner]: isNewGlobalPartner,
      })}
    >
      <div className={styles.basicInfoContent}>
        <LoaderOverlay show={isLoading} />

        <div className={styles.line}>
          <div className={styles.item}>
            <div className={styles.text}>
              <Translation text={TRANSLATION_KEYS.globalPartnerForm.formFields.globalPartnerRtm} />
            </div>
            <GlobalFormRtmSelect
              isNewGlobalPartner={isNewGlobalPartner}
              defaultValue={initialData.rtm}
            />
          </div>

          <div className={styles.item}>
            <div className={styles.text}>
              <Translation text={TRANSLATION_KEYS.globalPartnerForm.formFields.globalPartnerName} />
            </div>
            <FormInput
              isRequired
              formKey={GLOBAL_PARTNER_FORM_KEY}
              field="globalPartnerName"
              defaultValue={initialData.globalPartnerName}
              type="text"
              label="Partner Name"
              validationType={ValidationType.ONCHANGE}
            />
          </div>
        </div>

        <div className={styles.line}>
          <FormCheckBox
            formKey={GLOBAL_PARTNER_FORM_KEY}
            field="isIts"
            label="International Trade Specialist"
            defaultValue={initialData.isIts}
          />
        </div>

        <div className={styles.line}>
          <FormCheckBox
            formKey={GLOBAL_PARTNER_FORM_KEY}
            field="isReflectOnDashboards"
            label="Reflect on Dashboards"
            defaultValue={initialData.isReflectOnDashboards}
          />
        </div>

        <div className={styles.line}>
          <div className={styles.item}>
            <GlobalFormPbmSelect
              globalPartnerType={globalPartnerType}
              defaultValue={initialData.pbm}
            />
          </div>

          <div className={styles.item}>
            <GlobalFormManagerSelect defaultValue={initialData.managers} />
          </div>
        </div>

        <div className={cn(styles.line, styles.offerings)}>
          <OfferingsList offerings={offerings} />
        </div>

        <div className={styles.block}>
          <div className={styles.text}>
            <Translation
              text={TRANSLATION_KEYS.globalPartnerForm.formFields.globalPartnerExecutiveSummary}
            />
          </div>
          <FormTextarea
            rows={3}
            formKey={GLOBAL_PARTNER_FORM_KEY}
            field="executiveSummary"
            defaultValue={initialData.executiveSummary}
            type="text"
            label="Write some description"
          />
        </div>
      </div>

      <Actions
        className={styles.actions}
        position={'end'}
        items={[
          {
            id: 'apply',
            label: 'Apply',
            onClick: onApply,
            disabled: !isValid,
          },
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: onClose,
            variant: 'text',
            color: 'grey',
          },
        ]}
      />
    </div>
  )
}
